import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { v4 } from "uuid"
import api from "../api"
import { ReactComponent as AddIcon } from "../assets/images/icons/ic-add.svg"
import { ReactComponent as ArchiveIcon } from "../assets/images/icons/ic-archive.svg"
import { ReactComponent as SubIcon } from "../assets/images/icons/ic-min.svg"
import { ButtonStatus } from "../common/constants"
import Breadcrumb from "../components/Breadcrumb"
import Button from "../components/Button"
import Card from "../components/cards/Card"
import Checkbox from "../components/Checkbox"
import MultipleSelect from "../components/MultipleSelect"
import Select from "../components/Select"
import TestForm from "../components/tests/TestForm"
import TextInput from "../components/TextInput"
import { CorrectionType, TestStatus, TestType } from "../enums"
import typo from "../typography.module.css"
import { getDefaultTest, isTestValid, UTCtoLocal } from "../utils"
import styles from "./Test.module.css"

const Test = ({
    onBack = () => { },
    onSave = () => { },
    test = getDefaultTest()
}) => {
    const { t } = useTranslation()

    // test
    const [name, setName] = useState(test.name)
    const [description, setDescription] = useState(test.description)

    const [courses, setCourses] = useState([])
    const [editions, setEditions] = useState([])
    const [modules, setModules] = useState([])
    const [lessons, setLessons] = useState([])
    const [course, setCourse] = useState(null)
    const [edition, setEdition] = useState(null)
    const [module, setModule] = useState(null)
    const [lesson, setLesson] = useState(null)

    const [tags, setTags] = useState(test.tags)
    const [students, setStudents] = useState([])
    const [canBeRetried, setCanBeRetried] = useState(test.can_be_retried)
    const [expiresAt, setExpiresAt] = useState(test.expires_at ?? null)
    const [expiresIn, setExpiresIn] = useState(test.expires_in ? test.expires_in / 60 : null)
    const [publishedAt, setPublishedAt] = useState(test.published_at ?? null)
    const [correctionType, setCorrectionType] = useState(test.correction_type)
    const [successThreshold, setSuccessThreshold] = useState(test.success_threshold ?? 1)
    const [numberOfTests, setNumberOfTest] = useState(0)
    const [innerTests, setInnerTests] = useState([...test.content.innerTests])

    const [isValid, setIsValid] = useState(false)
    const [validationError, setValidationError] = useState("")
    const [invalidIndex, setInvalidIndex] = useState(null)
    const [isDraftable, setIsDraftable] = useState(test.name !== null && test.name !== "")
    const [loading, setLoading] = useState(false)
    const [draftButtonStatus, setDraftButtonStatus] = useState(null)
    const [publishButtonStatus, setPublishButtonStatus] = useState(null)
    const [excluded, setExcluded] = useState(test.excluded ?? [])
    const [currentTestId, setCurrentTestId] = useState(test.id ?? null)
    const [selectedQuestionIndex, setSelectedQuestionIndex] = useState(0)
    const [questionSearch, setQuestionSearch] = useState("")

    // Refs per ogni domanda
    const questionRefs = useRef([])
    const testFormUpdateTimeout = useRef(null)

    const questionLabel = useMemo(() => {
        return `${innerTests.length} ${innerTests.length === 1 ? 'domanda' : 'domande'}`
    }, [innerTests.length])

    const selectedQuestion = useMemo(() => {
        if (!innerTests || innerTests.length === 0) {
            return null
        }

        return innerTests[selectedQuestionIndex] ?? innerTests[0]
    }, [innerTests, selectedQuestionIndex])

    const getQuestionTitle = useCallback((question, index) => {
        const rawTitle = typeof question?.text === 'string' ? question.text : ''
        const compactTitle = rawTitle
            .replace(/[#*_`>[\]()!]/g, ' ')
            .replace(/\s+/g, ' ')
            .trim()

        if (compactTitle) {
            return compactTitle.length > 54 ? `${compactTitle.slice(0, 54)}...` : compactTitle
        }

        return `Domanda ${index + 1}`
    }, [])

    const filteredQuestionItems = useMemo(() => {
        const query = (questionSearch || "").trim().toLowerCase()

        return innerTests
            .map((question, index) => {
                const title = getQuestionTitle(question, index)
                return { question, index, title }
            })
            .filter(({ title }) => {
                if (!query) {
                    return true
                }

                return title.toLowerCase().includes(query)
            })
    }, [innerTests, questionSearch, getQuestionTitle])

    const correctionTypes = useMemo(() => {
        return [
            { id: 0, value: CorrectionType.Errors, name: t("tests.correctionType.errors") },
            { id: 1, value: CorrectionType.NumberOfErrors, name: t("tests.correctionType.nErrors") },
            { id: 2, value: CorrectionType.Solutions, name: t("tests.correctionType.solutions") },
        ]
    }, [])

    const canUpdateQuestions = useMemo(() => {
        if (test.status === TestStatus.Public) {
            if (test.published_at) {
                return new Date() < UTCtoLocal(test.published_at)
            }
            else {
                return false
            }
        }
        else if (test.status === TestStatus.Draft) {
            return true
        }
        else if (test.status === TestStatus.Completed) {
            return false
        }
        else return true
    }, [test])

    /* -------------------------------------------------------------------------- */
    /*                    HANDLER COURSE EDITION MODULE LESSON                    */
    /* -------------------------------------------------------------------------- */
    useEffect(() => {
        if (course) {
            getEditions()
        } else {
            setEditions([])
            setModules([])
            setLessons([])
        }
    }, [course])

    useEffect(() => {
        if (edition) {
            setModules(edition.modules.map(m => ({ ...m, label: m.name })))
        } else {
            setModules([])
            setLessons([])
        }
    }, [edition])

    useEffect(() => {
        if (module) {
            const { lessons } = module
            const l = lessons.map((l) => ({ ...l, label: l.name }))
            l.unshift({ id: null, name: t("tests.lesson.empty"), value: null })
            setLessons(l)
        } else {
            setLessons([])
        }
    }, [module])

    useEffect(() => {
        if (editions.length > 0 && test && !edition) {
            const e = editions.find(ee => ee.id === test.edition?.id)
            setEdition(e)
        }
    }, [editions])

    useEffect(() => {
        if (modules.length > 0 && test && !module) {
            const m = modules.find(mm => mm.id === test.module?.id)
            setModule(m)
        }
    }, [modules])

    useEffect(() => {
        if (lessons.length > 0 && test?.lesson && !lesson) {
            setLesson(lessons.find(l => l.id === test.lesson?.id))
        }
    }, [lessons])


    const getCourses = async () => {
        try {
            let courses = await api.get("/admin/courses");
            courses = courses.map(c => ({ ...c, label: c.name }))
            setCourses(courses.sort((a, b) => a.name.localeCompare(b.name)))
            if (test?.course) {
                const c = courses.find(cc => cc.id === test.course?.id)
                setCourse(c)
            }
        } catch (e) {
            console.error(e)
        }
    }

    const getEditions = async () => {
        try {
            let editions = await api.get(`/admin/courses/${course.id}/editions`);
            editions = editions.filter(e => e.modules && e.modules.length > 0)
            setEditions(editions.map(e => ({ ...e, label: e.name })))
        } catch (e) {
            console.error(e)
        }
    }

    /* ----------------------------------------------------------------------------- */

    useEffect(() => {
        const getTags = async () => {
            try {
                const res = await api.get("/admin/tags?type=general");
                setTags(res);
            } catch (e) {
                console.error(e);
            }
        };
        getCourses()
        getTags()
    }, []);

    useEffect(() => {
        const { isValid, isNameEmpty, areInnerTestValid, isModuleValid, isLessonValid } = isTestValid(name, module, lesson, innerTests)
        let validationError = ""
        let invalidIdx = null;
        if (isNameEmpty) {
            validationError = t("tests.validation.noName")
        } else if (!areInnerTestValid) {
            // Trova la prima domanda non valida
            for (let i = 0; i < innerTests.length; i++) {
                const innerTest = innerTests[i];
                let isCurrentValid = true;
                if (innerTest.isValid === false) isCurrentValid = false;
                if (isCurrentValid && !innerTest.testType) isCurrentValid = false;
                if (isCurrentValid && (!innerTest.text || innerTest.text.trim() === "")) isCurrentValid = false;
                if (isCurrentValid && (!innerTest.answers || innerTest.answers.length === 0)) isCurrentValid = false;
                if (isCurrentValid && (innerTest.testType === 'SingleChoice' || innerTest.testType === 'MultipleChoice')) {
                    const atLeastOneCorrect = innerTest.answers.some(a => a.isCorrect);
                    const atLeastOneIncorrect = innerTest.answers.some(a => !a.isCorrect);
                    const allAnswersHaveText = innerTest.answers.every(a => a.text && a.text.trim() !== "");
                    if (!atLeastOneCorrect || !atLeastOneIncorrect || !allAnswersHaveText) isCurrentValid = false;
                }
                if (isCurrentValid && innerTest.testType === 'TrueFalse') {
                    const allAnswersHaveText = innerTest.answers.every(a => a.text && a.text.trim() !== "");
                    const allAnswersHaveValue = innerTest.answers.every(a => a.value !== null && a.value !== undefined);
                    if (!allAnswersHaveText || !allAnswersHaveValue) isCurrentValid = false;
                }
                if (isCurrentValid && innerTest.testType === 'TextCompletion') {
                    if (!innerTest.words || innerTest.words.length === 0 || !innerTest.words.some(w => w.hidden === true)) isCurrentValid = false;
                }
                if (!isCurrentValid) {
                    invalidIdx = i;
                    break;
                }
            }
            if (invalidIdx !== null) {
                validationError = `Domanda ${invalidIdx + 1} non valida`;
            } else {
                validationError = t("tests.validation.innerTestsError");
            }
        } else if (!isModuleValid) {
            validationError = t("tests.validation.noModule")
        } else if (!isLessonValid) {
            validationError = ""
        }
        setValidationError(validationError)
        setInvalidIndex(invalidIdx)
        setIsValid(isValid)
    }, [innerTests, name, description, module, lesson, modules, lessons, expiresAt])

    useEffect(() => {
        let numberOfTests = 0
        for (const innerTest of innerTests) {
            const { testType } = innerTest
            switch (testType) {
                case TestType.SingleChoice:
                case TestType.MultipleChoice:
                    numberOfTests += 1
                    break
                case TestType.TrueFalse:
                    numberOfTests += innerTest.answers.length
                    break
                case TestType.TextCompletion:
                    if (innerTest.words) {
                        numberOfTests += innerTest.words.reduce((a, c) => {
                            if (c.hidden) {
                                a += 1
                            }
                            return a
                        }, 0)
                    }
                    break
                default: break;
            }
        }
        setNumberOfTest(numberOfTests)
    }, [innerTests])

    useEffect(() => {
        const getStudents = async (moduleId) => {
            try {
                const res = await api.get(`/admin/modules/${moduleId}/students`);
                setStudents(res.sort((a, b) => a.surname.localeCompare(b.surname))
                    .map((s) => ({ id: s.id, name: `${s.surname} ${s.name}`, value: s.id })));
            } catch (e) {
                console.error(e);
            }
        };
        if (module) {
            getStudents(module.id)
        }
    }, [module])

    const addQuestion = useCallback(() => {
        setInnerTests((prev) => {
            const next = [...prev, {
                testType: TestType.SingleChoice, text: "",
                tags: [],
                answers: [{
                    id: v4(),
                    text: "",
                    isCorrect: false
                }, {
                    id: v4(),
                    text: "",
                    isCorrect: false
                }],
            }]

            setSelectedQuestionIndex(next.length - 1)
            return next
        })
    }, [])

    const removeQuestion = useCallback((removedIndex) => {
        setInnerTests((prev) => {
            if (prev.length <= 1) {
                return prev
            }

            const next = prev.filter((_, index) => index !== removedIndex)

            setSelectedQuestionIndex((current) => {
                if (current > removedIndex) {
                    return current - 1
                }

                if (current === removedIndex) {
                    return Math.max(0, removedIndex - 1)
                }

                return current
            })

            return next
        })
    }, [])

    const onTestFormChange = useCallback((changes, index) => {
        const { testType, innerTest } = changes

        if (testFormUpdateTimeout.current) {
            clearTimeout(testFormUpdateTimeout.current)
        }

        testFormUpdateTimeout.current = setTimeout(() => {
            setInnerTests((prev) => {
                const next = [...prev]
                next[index] = { testType, ...innerTest }
                return next
            })
        }, 150)
    }, [])

    useEffect(() => {
        return () => {
            if (testFormUpdateTimeout.current) {
                clearTimeout(testFormUpdateTimeout.current)
            }
        }
    }, [])

    useEffect(() => {
        if (innerTests.length === 0) {
            setSelectedQuestionIndex(0)
            return
        }

        if (selectedQuestionIndex > innerTests.length - 1) {
            setSelectedQuestionIndex(innerTests.length - 1)
        }
    }, [innerTests.length, selectedQuestionIndex])

    const saveTest = async (status) => {
        setLoading(true)
        if (status === TestStatus.Draft) {
            setDraftButtonStatus(ButtonStatus.Loading)
        } else {
            setPublishButtonStatus(ButtonStatus.Loading)
        }
        const editing = !!currentTestId
        const it = innerTests.map((t) => {
            return {
                ...t,
                id: t.id ?? v4()
            }
        })
        try {
            console.log({ course, module, lesson })
            const newTest = {
                name,
                description,
                can_be_retried: canBeRetried,
                status,
                correction_type: correctionType,
                expires_at: expiresAt ? UTCtoLocal(expiresAt) : null,
                success_threshold: successThreshold > it.length ? it.length : successThreshold,
                content: { innerTests: it },
                publishable: isValid,
                expires_in: expiresIn ? parseInt(expiresIn * 60) : null,
                published_at: publishedAt ? UTCtoLocal(publishedAt) : null,
                excluded: excluded ?? [],
            }
            if (module) {
                newTest.module_id = module.id
            }

            if (lesson) {
                newTest.lesson_id = lesson.id
            }

            const response = editing ?
                await api.put(`/admin/tests/${currentTestId}`, newTest) :
                await api.post("/admin/tests", newTest)

            const savedTestId = response?.id ?? response?.test?.id ?? currentTestId
            if (savedTestId && savedTestId !== currentTestId) {
                setCurrentTestId(savedTestId)
            }

            if (status === TestStatus.Draft) {
                setDraftButtonStatus(ButtonStatus.Success)
            } else {
                setPublishButtonStatus(ButtonStatus.Success)
            }
        } catch (e) {
            console.error(e)
            setValidationError(e.message)
            if (status === TestStatus.Draft) {
                setDraftButtonStatus(ButtonStatus.Error)
            } else {
                setPublishButtonStatus(ButtonStatus.Error)
            }
        }
        setLoading(false)

    }

    useEffect(() => {
        setIsDraftable(name !== null && name !== "")
    }, [name])


    return (
        <div className={styles.container}>
            <Breadcrumb items={[{ label: "Didattica" }, { label: "Test", to: "/tests", onClick: event => { event.preventDefault(); onBack() } }, { label: currentTestId ? "Modifica test" : "Nuovo test" }]} />
                <div className={styles.heroHeader}>
                    <div>
                        <h1 className={styles.pageTitle}>{currentTestId ? "Modifica test" : "Nuovo test"}</h1>
                        <div className={styles.pageSubtitle}>Configura regole, destinatari e domande in un unico flusso.</div>
                    </div>
                    <div className={styles.heroStats}>
                        <div className={styles.statPill}>{questionLabel}</div>
                        <div className={styles.statPill}>Soglia {successThreshold}/{numberOfTests}</div>
                    </div>
                </div>

            <Card appearance="modern" title="Informazioni test">
                <div className={styles.section}>

                    <div className={styles.metaGrid}>
                        <div className={styles.metaPanelWide}>
                            <TextInput appearance="modern"
                                type={"text"}
                                label={"Nome"}
                                placeholder={"Nome"}
                                value={name}
                                onKeyUp={(value) => {
                                    setName(value)
                                }} />

                            <label className={styles.pathLabel} htmlFor="test-description">Descrizione</label>
                            <textarea id="test-description" className={styles.textArea} value={description} placeholder="Descrizione" onChange={(e) => setDescription(e.target.value)} />
                        </div>

                        <div className={styles.metaPanelWide}>
                            <div className={styles.fieldLabel}>Percorso didattico</div>
                            <div className={styles.pathGrid}>
                                <div className={styles.pathField}>
                                    <div className={styles.pathLabel}>Corso</div>
                                    <Select appearance="modern" portal nullable nullLabel="Nessun corso" options={courses} disabled={!courses || courses.length === 0} selected={courses.find(c => c.id === course?.id)} onSelect={(c) => {
                                        setCourse(c)
                                        if (c?.id !== course?.id) {
                                            setEdition(null)
                                            setModule(null)
                                            setLesson(null)
                                        }
                                    }} placeholder={"Corso"} />
                                </div>

                                <div className={styles.pathField}>
                                    <div className={styles.pathLabel}>Edizione</div>
                                    <Select appearance="modern" portal options={editions} disabled={!editions || editions.length === 0} selected={editions.find(e => e.id === edition?.id)} onSelect={(e) => {
                                        setEdition(e)
                                        setModule(null)
                                        setLesson(null)
                                    }} placeholder={"Edizione"} />
                                </div>

                                <div className={styles.pathField}>
                                    <div className={styles.pathLabel}>Modulo</div>
                                    <Select appearance="modern" portal options={modules} disabled={!modules || modules.length === 0} selected={modules.find(m => m.id === module?.id)} onSelect={(m) => {
                                        setModule(m)
                                        setLesson(null)
                                    }} placeholder={"Modulo"} />
                                </div>

                                <div className={styles.pathField}>
                                    <div className={styles.pathLabel}>Lezione</div>
                                    <Select appearance="modern" portal options={lessons} disabled={!lessons || lessons.length === 0} selected={lessons.find(l => l.id === lesson?.id)} onSelect={(l) => {
                                        setLesson(l)
                                    }} placeholder={"Lezione"} />
                                </div>
                            </div>
                        </div>

                        <div className={styles.metaPanelWide}>
                            <div className={styles.fieldLabel}>Impostazioni test</div>
                            <div className={styles.settingsGrid}>
                                <div className={styles.settingsField}>
                                    <div className={styles.pathLabel}>{t("tests.correctionType.label")}</div>
                                    <Select appearance="modern" portal options={correctionTypes} disabled={!correctionTypes || correctionTypes.length === 0} selected={correctionTypes.find(c => c.value === correctionType) ?? correctionTypes[0]} onSelect={(c) => {
                                        setCorrectionType(c.value)
                                    }} placeholder={t("tests.correctionType.placeholder")} />
                                </div>

                                <div className={styles.settingsField}>
                                    <div className={styles.pathLabel}>Soglia di superamento</div>
                                    <div className={styles.threshold}>
                                        <button type="button" aria-label="Riduci soglia" className={styles.successButton} onClick={() => setSuccessThreshold(s => Math.max(1, s - 1))}><SubIcon /></button>
                                        <div className={styles.values}>
                                            <span>Soglia</span>
                                            <b>{successThreshold}</b>
                                            <span>/ {numberOfTests}</span>
                                        </div>
                                        <button type="button" aria-label="Aumenta soglia" className={styles.successButton} onClick={() => setSuccessThreshold(s => Math.min(numberOfTests, s + 1))}><AddIcon /></button>
                                    </div>
                                </div>

                                <div className={styles.settingsField}>
                                    <TextInput appearance="modern"
                                        label={"Durata (m)"}
                                        placeholder={"Durata (m)"}
                                        type={"number"}
                                        onKeyUp={(v) => {
                                            setExpiresIn(v)
                                        }}
                                        value={expiresIn}
                                        defaultValue={test.expires_in ?? null}

                                    />
                                </div>

                                <div className={styles.settingsField}>
                                    <div className={styles.checkboxRow}>
                                        <Checkbox label={t("tests.canBeRetried")} value={canBeRetried} onChange={(isChecked) => {
                                            setCanBeRetried(isChecked)
                                        }} />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className={styles.metaPanel}>
                            <TextInput appearance="modern"
                                label={"Data di pubblicazione"}
                                placeholder={"Data di pubblicazione"}
                                disabled={!canUpdateQuestions}
                                type={"date"}
                                onKeyUp={(d) => {
                                    setPublishedAt(d ? d.toISOString().split('.')[0] : null)
                                }}
                                value={publishedAt ? new Date(publishedAt + 'Z') : null}
                                defaultValue={publishedAt ? new Date(publishedAt + 'Z') : null}
                            />
                        </div>

                        <div className={styles.metaPanel}>
                            <TextInput appearance="modern"
                                label={"Data di scadenza"}
                                placeholder={"Data di scadenza"}
                                type={"date"}
                                onKeyUp={(d) => {
                                    setExpiresAt(d ? d.toISOString().split('.')[0] : null)
                                }}
                                value={expiresAt ? new Date(expiresAt + 'Z') : null}
                                min={new Date(test.published_at)}
                                defaultValue={expiresAt ? new Date(expiresAt + 'Z') : null}
                            />
                        </div>



                        <div className={styles.metaPanelWide}>
                            <div className={styles.fieldLabel}>Destinatari</div>
                            <MultipleSelect appearance="modern" portal
                                disabled={!students || students.length === 0 || !edition}
                                selected={students?.filter(e => !excluded.includes(e.id))}
                                options={students}
                                allLabel="Tutti gli studenti"
                                placeholder={"Seleziona Studenti"}
                                onSelect={(items) => {
                                    setExcluded(students.filter(e => !items.map(i => i.id).includes(e.id)).map(e => e.id))
                                }} />
                        </div>
                    </div>
                </div>

            </Card>
            <main className={styles.questionsColumn}>
                <div className={styles.section}>
                    <div className={styles.questionsHeader}>
                        <div>
                            <div className={typo.subtitle}>{t("tests.questions")}</div>
                            {!canUpdateQuestions &&
                                <div className={typo.caption} style={{ color: 'red' }}>Non puoi modificare le domande di un test gia pubblicato.</div>
                            }
                        </div>
                    </div>

                    <div className={styles.questionWorkspace}>
                        <div className={styles.questionSidebar}>
                            <div className={styles.questionSearch}>
                                <TextInput appearance="modern"
                                    type={"search"}
                                    placeholder={"Cerca domanda per titolo"}
                                    value={questionSearch}
                                    onKeyUp={(value) => {
                                        if (typeof value === 'string') {
                                            setQuestionSearch(value)
                                            return
                                        }

                                        setQuestionSearch(value?.target?.value ?? "")
                                    }}
                                />
                            </div>

                            <div className={styles.questionList}>
                                {filteredQuestionItems.map(({ question, index, title }) => {
                                    const hasError = question.isValid === false
                                    const isActive = index === selectedQuestionIndex
                                    const questionTags = Array.isArray(question?.tags) ? question.tags : []

                                    return (
                                        <button
                                            key={`question-${index}`}
                                            type="button"
                                            className={`${styles.questionItem} ${isActive ? styles.questionItemActive : ''}`}
                                            onClick={() => {
                                                setSelectedQuestionIndex(index)
                                            }}
                                        >
                                            <div className={styles.questionItemMeta}>
                                                <span className={styles.questionItemIndex}>Domanda {index + 1}</span>
                                                <span className={styles.questionItemTitle}>{title}</span>
                                                {questionTags.length > 0 && (
                                                    <div className={styles.questionItemTags}>
                                                        {questionTags.slice(0, 2).map((tag, tagIndex) => (
                                                            <span key={`question-${index}-tag-${tagIndex}`} className={styles.questionTagChip}>
                                                                {tag.name || tag.label || tag.value || `Tag ${tagIndex + 1}`}
                                                            </span>
                                                        ))}
                                                        {questionTags.length > 2 && (
                                                            <span className={styles.questionTagChip}>+{questionTags.length - 2}</span>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                            {hasError && <span className={styles.questionItemBadge}>Errore</span>}
                                        </button>
                                    )
                                })}

                                {filteredQuestionItems.length === 0 && (
                                    <div className={styles.noQuestionResults}>Nessuna domanda trovata</div>
                                )}
                            </div>

                            <div className={styles.questionSidebarFooter}>
                                <button className={`${styles.addQuestionButton} ${!canUpdateQuestions ? styles.disabled : ''}`} onClick={addQuestion}>
                                    + {t("tests.addQuestion")}
                                </button>
                            </div>
                        </div>

                        <div className={styles.questionEditorPane}>
                            {selectedQuestion && (() => {
                                const { testType, ...innerTest } = selectedQuestion

                                return (
                                    <div className={styles.questionBlock} ref={el => questionRefs.current[selectedQuestionIndex] = el}>
                                        <div className={styles.questionTitleRow}>
                                            <div className={styles.questionTitle}>Domanda {selectedQuestionIndex + 1}</div>
                                        </div>
                                        <TestForm
                                            key={`selected-question-${selectedQuestionIndex}`}
                                            disabled={!canUpdateQuestions}
                                            innerTest={innerTest}
                                            tagList={tags}
                                            testType={testType}
                                            onRemove={() => removeQuestion(selectedQuestionIndex)}
                                            onChange={(changes) => onTestFormChange(changes, selectedQuestionIndex)}
                                            showRemoveButton={innerTests.length > 1}
                                        />
                                    </div>
                                )
                            })()}
                        </div>
                    </div>
                </div>
            </main>
            <div className={styles.actionsContainer + ' ' + styles.stickyActionsContainer}>
                {
                    validationError &&
                    <div className={styles.validationError} style={{ width: '100%' }}>
                        {invalidIndex !== null ? (
                            <span
                                style={{ cursor: 'pointer', textDecoration: 'underline' }}
                                onClick={() => {
                                    setSelectedQuestionIndex(invalidIndex)
                                    const el = questionRefs.current[invalidIndex];
                                    if (el && typeof el.scrollIntoView === 'function') {
                                        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                    }
                                }}
                            >
                                {validationError}
                            </span>
                        ) : validationError}
                    </div>
                }
                {!validationError && lesson?.id &&
                    <div style={{ padding: '0 1rem', width: '100%' }}>
                        I test associati alle lezioni possono essere salvati solo in bozza e pubblicati durante lo svolgimento della lezione direttamente dalla pagina della lezione stessa.
                    </div>
                }
                <div className={styles.actions}>
                    {![TestStatus.Public, TestStatus.Completed].includes(test.status) &&
                        <Button
                            appearance="subtle"
                            accentColor={"var(--primary)"}
                            onClick={() => saveTest(TestStatus.Draft)}
                            status={draftButtonStatus}
                            disabled={!isDraftable || loading}
                            style={{
                                padding: ".7rem 1.5rem",
                                fontWeight: 600
                            }}
                        >
                            {t("tests.draft")}
                            <ArchiveIcon />
                        </Button>
                    }
                    <Button
                        onClick={() => saveTest(TestStatus.Public)}
                        disabled={!isValid || lesson?.id || loading}
                        status={publishButtonStatus}
                        style={{
                            padding: ".7rem 1.5rem",
                            fontWeight: 600
                        }}
                    >
                        {t("tests.publish")}
                    </Button>
                </div>
            </div>
        </div>
    )
}

export default Test
